// src/components/anchors/AnchorsManager.jsx
// Reescritura completa (UI moderna) con "hotspots por proximidad":
//  - Hotspots SOLO con punto (sin etiqueta textual).
//  - Cuando el cursor/tu dedo se acerca a un hotspot, se abre el contenido:
//      * tag/image/audio/video: abre el panel de detalle automáticamente.
//      * link: abre el enlace en una pestaña nueva (con una leve demora-dwell).
//  - Dwell (demora) configurable y radio de proximidad relativo al tamaño del modelo.
//  - Fix del offset en el pick (usa clientX/clientY del viewport).
//  - Soporte unificado mouse/pointer/touch. Mantiene TODA la funcionalidad previa.
//  - Estética más moderna y elegante (estilos mejorados vía clases; ver anchors.css).

import React from 'react';
import {
  collection, addDoc, getDocs, deleteDoc, doc,
  updateDoc, serverTimestamp, query, orderBy
} from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './anchors.css';

/****************************** Loader <model-viewer> ******************************/
let mvScriptPromise = null;
function ensureModelViewer() {
  if (customElements.get('model-viewer')) return Promise.resolve();
  if (!mvScriptPromise) {
    mvScriptPromise = import('@google/model-viewer/dist/model-viewer.min.js').then(async () => {
      if (customElements.whenDefined) {
        try { await customElements.whenDefined('model-viewer'); } catch {}
      }
    });
  }
  return mvScriptPromise;
}

/****************************** Utilidades ******************************/
const emptyAnchor = () => ({
  title: '',
  type: 'tag',        // tag | image | audio | link | video
  text: '',
  mediaUrl: '',
  position: { x: 0, y: 0, z: -0.5 },
  rotation: { x: 0, y: 0, z: 0 }, // pitch (X), yaw (Y), roll (Z)
  visible: true,
  normal: { x: 0, y: 1, z: 0 },
});

function normalToEuler(nx, ny, nz) {
  const toDeg = (r) => (r * 180) / Math.PI;
  const yaw = Math.atan2(nx, nz);
  const pitch = -Math.asin(Math.max(-1, Math.min(1, ny)));
  return { x: toDeg(pitch), y: toDeg(yaw), z: 0 };
}

const vecToAttr = (v) => `${(v?.x ?? 0).toFixed(4)} ${(v?.y ?? 0).toFixed(4)} ${(v?.z ?? 0).toFixed(4)}`;
function normalToAttr(n) {
  const nx = n?.x ?? 0, ny = n?.y ?? 1, nz = n?.z ?? 0;
  const len = Math.hypot(nx, ny, nz) || 1;
  return `${(nx/len).toFixed(4)} ${(ny/len).toFixed(4)} ${(nz/len).toFixed(4)}`;
}
function isValidUrl(v) {
  if (!v || typeof v !== 'string') return false;
  try { const u = new URL(v); return !!u.protocol && !!u.host; } catch { return false; }
}
function clientXY(evt) {
  if (typeof evt.clientX === 'number' && typeof evt.clientY === 'number') {
    return { clientX: evt.clientX, clientY: evt.clientY };
  }
  const t = evt.touches?.[0] || evt.changedTouches?.[0];
  if (t) return { clientX: t.clientX, clientY: t.clientY };
  return null;
}

/****************************** Parámetros de proximidad ******************************/
const PROXIMITY_RATIO = 0.035;   // fracción del diagonal del modelo (radio efectivo)
const DWELL_MS = 320;            // demora antes de abrir el hotspot
const COOLDOWN_MS = 900;         // anti-spam al abrir repetidamente

/****************************** Componente ******************************/
export default function AnchorsManager({ model, onClose }) {
  const [anchors, setAnchors]   = React.useState([]);
  const [selected, setSelected] = React.useState(null);
  const [active, setActive]     = React.useState(null);
  const [saving, setSaving]     = React.useState(false);
  const [loading, setLoading]   = React.useState(true);

  const [mvScriptReady, setMvScriptReady] = React.useState(false);
  const [mvLoading, setMvLoading]         = React.useState(true);
  const [mvError, setMvError]             = React.useState(null);

  const [pickMode, setPickMode] = React.useState(false);
  const [preview, setPreview]   = React.useState(null); // {position, normal}

  const mvRef = React.useRef(null);

  // Estado de cámara vs posición original (para opacar hotspots)
  const [atOrigin, setAtOrigin] = React.useState(true);
  const initialOrbitRef = React.useRef(null);

  // Proximidad
  const [nearestId, setNearestId] = React.useState(null);
  const dwellTimerRef = React.useRef(null);
  const lastOpenRef = React.useRef({ id: null, ts: 0 });
  const rafRef = React.useRef(null);
  const diagRef = React.useRef(null); // diagonal del modelo

  const forceDoubleSidedMaterials = React.useCallback((el) => {
    try {
      const materials = el?.model?.materials;
      if (!Array.isArray(materials)) return;
      materials.forEach((mat) => {
        try {
          if (typeof mat?.setDoubleSided === 'function') {
            mat.setDoubleSided(true);
            return;
          }
          if (Object.prototype.hasOwnProperty.call(mat || {}, 'doubleSided')) {
            mat.doubleSided = true;
          }
        } catch {}
      });
    } catch {}
  }, []);

  React.useEffect(() => {
    let alive = true;
    ensureModelViewer()
      .then(() => { if (alive) setMvScriptReady(true); })
      .catch(() => { if (alive) setMvScriptReady(true); });
    return () => { alive = false; };
  }, []);

  React.useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const q = query(
          collection(db, 'models', model.id, 'anchors'),
          orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
        setAnchors(list);
      } catch (e) {
        console.error('Error loading anchors:', e);
      } finally {
        setLoading(false);
      }
    };
    if (model?.id) load();
  }, [model?.id]);

  React.useEffect(() => {
    const el = mvRef.current;
    if (!el) return;

    const onLoad = () => {
      setMvLoading(false);
      setMvError(null);
      try {
        forceDoubleSidedMaterials(el);
        const o = el.getCameraOrbit?.();
        if (o) initialOrbitRef.current = { theta: o.theta, phi: o.phi, radius: o.radius };
        const dim = el.getDimensions?.();
        if (dim) {
          const diag = Math.hypot(dim.x, dim.y, dim.z);
          diagRef.current = diag > 0 ? diag : 1;
        } else {
          diagRef.current = 1;
        }
      } catch {}
      setAtOrigin(true);
    };
    const onError = (ev) => {
      setMvLoading(false);
      setMvError(ev?.detail?.message || 'Error al cargar el modelo');
    };
    const onArStatus = (ev) => {
      if (ev?.detail?.status === 'session-started') {
        forceDoubleSidedMaterials(el);
      }
    };

    setMvLoading(true); setMvError(null);
    el.addEventListener('load', onLoad);
    el.addEventListener('error', onError);
    el.addEventListener('ar-status', onArStatus);

    const onCameraChange = () => {
      try {
        const base = initialOrbitRef.current;
        const o = el.getCameraOrbit?.();
        if (!base || !o) return;
        const dTheta = Math.abs(o.theta - base.theta);
        const dPhi   = Math.abs(o.phi - base.phi);
        const dRad   = Math.abs(o.radius - base.radius);
        const within = dTheta < 0.05 && dPhi < 0.05 && dRad < 0.02;
        setAtOrigin(within);
      } catch {}
    };
    el.addEventListener('camera-change', onCameraChange);

    if (el.loaded) {
      onLoad();
    }

    // Proximidad: pointermove → calcular punto sobre la malla → buscar hotspot cercano
    const onPointerMove = (evt) => {
      if (pickMode) return; // en modo pick no disparamos proximidad
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(async () => {
        try {
          const pt = clientXY(evt);
          if (!pt || !el?.positionAndNormalFromPoint) return;
          const hit = await el.positionAndNormalFromPoint(pt.clientX, pt.clientY);
          if (!hit) { setNearestId(null); return; }

          const { position } = hit;
          const diag = diagRef.current || 1;
          const radius = PROXIMITY_RATIO * diag;

          let best = { id: null, d: Infinity, anchor: null };
          for (const a of anchors) {
            if (a.visible === false || !a?.position) continue;
            const dx = (a.position.x ?? 0) - position.x;
            const dy = (a.position.y ?? 0) - position.y;
            const dz = (a.position.z ?? 0) - position.z;
            const d = Math.hypot(dx, dy, dz);
            if (d < best.d) best = { id: a.id, d, anchor: a };
          }

          if (best.id && best.d <= radius) {
            if (nearestId !== best.id) {
              setNearestId(best.id);
              // reiniciar dwell
              if (dwellTimerRef.current) { clearTimeout(dwellTimerRef.current); }
              dwellTimerRef.current = setTimeout(() => {
                // cooldown
                const now = Date.now();
                const since = now - (lastOpenRef.current.ts || 0);
                if (lastOpenRef.current.id === best.id && since < COOLDOWN_MS) return;

                // abrir según tipo
                const a = best.anchor;
                if (a?.type === 'link' && (isValidUrl(a.mediaUrl) || isValidUrl(a.text))) {
                  const href = isValidUrl(a.mediaUrl) ? a.mediaUrl : a.text;
                  try { window.open(href, '_blank', 'noopener,noreferrer'); } catch {}
                  lastOpenRef.current = { id: best.id, ts: Date.now() };
                } else {
                  setActive(a);
                  lastOpenRef.current = { id: best.id, ts: Date.now() };
                }
              }, DWELL_MS);
            }
          } else {
            setNearestId(null);
            if (dwellTimerRef.current) { clearTimeout(dwellTimerRef.current); dwellTimerRef.current = null; }
          }
        } catch (e) {
          // silencioso
        }
      });
    };

    el.addEventListener('pointermove', onPointerMove, { passive: true });

    return () => {
      el.removeEventListener('load', onLoad);
      el.removeEventListener('error', onError);
      el.removeEventListener('ar-status', onArStatus);
      el.removeEventListener('camera-change', onCameraChange);
      el.removeEventListener('pointermove', onPointerMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (dwellTimerRef.current) clearTimeout(dwellTimerRef.current);
    };
  }, [model?.id, mvScriptReady, pickMode, anchors, nearestId, forceDoubleSidedMaterials]);

  // Toggle controles cuando entro en modo pick
  React.useEffect(() => {
    const el = mvRef.current;
    if (!el) return;
    el.cameraControls = !pickMode;
    el.style.cursor = pickMode ? 'crosshair' : 'grab';
  }, [pickMode]);

  // PICK con fix de offset
  const onModelClick = async (ev) => {
    if (!pickMode) return;
    const mv = mvRef.current;
    if (!mv || typeof mv.positionAndNormalFromPoint !== 'function') return;
    try {
      const pt = clientXY(ev);
      if (!pt) return;
      const hit = await mv.positionAndNormalFromPoint(pt.clientX, pt.clientY);
      if (!hit) return;

      const { position, normal } = hit;
      const rot = normalToEuler(normal.x, normal.y, normal.z);

      setSelected((s) => {
        const base = s || emptyAnchor();
        return {
          ...base,
          position: { x: position.x, y: position.y, z: position.z },
          rotation: rot,
          normal: { x: normal.x, y: normal.y, z: normal.z }
        };
      });
      setPreview({ position, normal });
    } catch (e) {
      console.error('Hit-test error:', e);
    }
  };

  /*************** Guardar / actualizar / borrar ***************/
  async function handleSave() {
    if (!selected) return;
    setSaving(true);
    try {
      if (selected.id) {
        await updateDoc(doc(db, 'models', model.id, 'anchors', selected.id), {
          ...selected,
          updatedAt: serverTimestamp()
        });
        setAnchors(prev => prev.map(a => (a.id === selected.id ? selected : a)));
      } else {
        const ref = await addDoc(collection(db, 'models', model.id, 'anchors'), {
          ...selected,
          createdAt: serverTimestamp()
        });
        setAnchors(prev => [{ id: ref.id, ...selected }, ...prev]);
      }
      setSelected(null);
      setPickMode(false);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!id) return;
    if (!window.confirm('¿Eliminar accesorio/ancla?')) return;
    try {
      await deleteDoc(doc(db, 'models', model.id, 'anchors', id));
      setAnchors(prev => prev.filter(a => a.id !== id));
      if (selected?.id === id) setSelected(null);
      if (active?.id === id) setActive(null);
    } catch (e) {
      console.error(e);
    }
  }

  /*************** Compatibilidad & preview ***************/
  const modelUrl = model?.modelUrl || '';
  const urlNoParams = modelUrl.split('?')[0] || '';
  const ext = urlNoParams.split('.').pop()?.toLowerCase();
  const isGLTF = ext === 'glb' || ext === 'gltf';
  const isUSDZ = ext === 'usdz';
  const supported = isGLTF || isUSDZ;

  const previewPosAttr = preview?.position ? vecToAttr(preview.position) : null;
  const previewNorAttr = preview?.normal   ? normalToAttr(preview.normal)   : null;

  /*************** UI styles mínimos (estructura) ***************/
  const viewerShellStyle = {
    width: '100%', minHeight: '60vh', height: '100%', position: 'relative',
    background: 'linear-gradient(180deg,#0b0f1a 0%, #0e1425 100%)',
    borderRadius: 16, overflow: 'hidden',
    boxShadow: '0 10px 35px rgba(0,0,0,.35)'
  };
  const modelViewerStyle = { display: 'block', width: '100%', height: '100%', minHeight: '60vh' };

  const hotspotStyleCommon = React.useMemo(() => ({
    opacity: atOrigin ? 1 : 0.35, transition: 'opacity .2s ease'
  }), [atOrigin]);

  /*************** Render ***************/
  return (
    <div className="anchors-wrap modern-ui">
      <div className="anchors-header glossy">
        <div className="anchors-title">
          <i className="bi bi-pin-map-fill"></i>
          Accesorios & Anclas (AR)
        </div>
        <div className="anchors-subtitle">Modelo: <b>{model?.name || model?.id}</b></div>
        <div className="anchors-actions">
          <button className="btn-secondary" onClick={onClose}>
            <i className="bi bi-x-lg"></i> Cerrar
          </button>
        </div>
      </div>

      <div className="anchors-grid">
        {/* VISOR */}
        <div className={`anchors-viewer ${pickMode ? 'pick-on' : ''}`} style={{minHeight:'60vh'}}>
          {!modelUrl ? (
            <div className="viewer-empty">
              <i className="bi bi-cube"></i>
              <p>Este modelo no tiene URL definida</p>
            </div>
          ) : !supported ? (
            <div className="viewer-empty">
              <i className="bi bi-exclamation-triangle"></i>
              <p>Formato no soportado ({ext}). Usa .glb, .gltf o .usdz.</p>
            </div>
          ) : (
            <div className="viewer-stage" style={viewerShellStyle}>
              <model-viewer
                key={model.id}
                ref={mvRef}
                src={modelUrl}
                {...(isUSDZ ? { 'ios-src': modelUrl } : {})}
                ar
                ar-modes="webxr scene-viewer quick-look"
                camera-controls
                alt={model?.name || 'Modelo 3D'}
                style={modelViewerStyle}
                loading="eager"
                environment-image="neutral"
                exposure="1.0"
                crossorigin="anonymous"
                reveal="auto"
                interaction-prompt="auto"
                onClick={onModelClick}
                onPointerDown={onModelClick}
                onTouchEnd={onModelClick}
              >
                {/* Hotspots guardados: SOLO punto (sin etiqueta). */}
                {anchors
                  .filter(a => a.visible !== false && a?.position)
                  .map((a) => (
                    <button
                      key={`hotspot-${a.id}`}
                      slot={`hotspot-${a.id}`}
                      className={`hotspot saved dot-only ${a.id===nearestId ? 'proximal' : ''} ${a.type}`}
                      data-position={vecToAttr(a.position)}
                      data-normal={a.normal ? normalToAttr(a.normal) : '0 1 0'}
                      title={a.title || 'Accesorio'}
                      onClick={(e) => {
                        e?.stopPropagation?.();
                        // Click directo: abre como antes
                        if (a?.type === 'link') {
                          const href = isValidUrl(a.mediaUrl) ? a.mediaUrl : (isValidUrl(a.text) ? a.text : null);
                          if (href) { window.open(href, '_blank', 'noopener,noreferrer'); return; }
                        }
                        setActive(a);
                      }}
                      style={hotspotStyleCommon}
                      aria-label={a.title || 'Accesorio'}
                    >
                      <span className="hotspot-dot" />
                      {/* sin etiqueta textual */}
                    </button>
                  ))}

                {/* Hotspot de vista previa (punto) */}
                {previewPosAttr && previewNorAttr && (
                  <button
                    slot="hotspot-preview"
                    className="hotspot preview dot-only"
                    data-position={previewPosAttr}
                    data-normal={previewNorAttr}
                    title="Vista previa"
                    style={hotspotStyleCommon}
                  >
                    <span className="hotspot-dot" />
                  </button>
                )}
              </model-viewer>

              {/* Overlays de estado */}
              {(mvLoading || mvError || !mvScriptReady) && (
                <div className="viewer-overlay" style={{ pointerEvents: 'none' }}>
                  {!mvScriptReady && (
                    <div className="viewer-status"><div className="spinner" /><span>Inicializando visor 3D…</span></div>
                  )}
                  {mvScriptReady && mvLoading && !mvError && (
                    <div className="viewer-status"><div className="spinner" /><span>Cargando modelo…</span></div>
                  )}
                  {mvError && (
                    <div className="viewer-error">
                      <i className="bi bi-bug"></i>
                      <div>
                        <b>No se pudo mostrar el modelo.</b>
                        <div className="small">{String(mvError)}</div>
                        <div className="small">Verifica permisos públicos y el <code>Content-Type</code> en Storage.</div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {isUSDZ && (
                <div className="viewer-warning">
                  <i className="bi bi-info-circle"></i>
                  <span>
                    Este archivo es <b>.usdz</b>. El preview web puede no renderizarse;
                    usá iOS Quick Look o subí un <b>.glb/.gltf</b> para vista web.
                  </span>
                </div>
              )}

              {pickMode && (
                <div className="pick-hint">
                  <i className="bi bi-mouse3"></i> Click/tap en el modelo para fijar posición y rotación
                </div>
              )}
            </div>
          )}
        </div>

        {/* EDITOR */}
        <div className="anchors-editor cardish">
          <div className="editor-toolbar">
            <button className="btn-primary" onClick={() => { setSelected(emptyAnchor()); setPreview(null); }}>
              <i className="bi bi-plus-lg"></i> Nuevo accesorio
            </button>
            <button
              className={`btn ${pickMode ? 'btn-active' : ''}`}
              onClick={() => { if (!selected) setSelected(emptyAnchor()); setPickMode(v => !v); }}
              disabled={!modelUrl || !supported}
              title="Elegir con click en el modelo"
            >
              <i className="bi bi-bullseye"></i> {pickMode ? 'Salir de pick' : 'Elegir con click'}
            </button>
          </div>

          <div className="anchors-list">
            {loading ? (
              <div className="loading-state" style={{ margin: 0 }}>
                <div className="spinner" />
                <p>Cargando accesorios...</p>
              </div>
            ) : anchors.length === 0 ? (
              <div className="empty-state" style={{ margin: 0 }}>
                <i className="bi bi-pin"></i>
                <p>Aún no hay accesorios</p>
              </div>
            ) : (
              anchors.map(a => (
                <div
                  key={a.id}
                  className={`anchor-item ${selected?.id === a.id ? 'active' : ''}`}
                  onClick={() => {
                    setSelected({ ...a });
                    if (a?.position) setPreview({ position: a.position, normal: a.normal || { x: 0, y: 1, z: 0 } });
                  }}
                >
                  <div className="anchor-title">
                    <span className="anchor-dot" />
                    {a.title || '(sin título)'}
                  </div>
                  <div className="anchor-meta">
                    <span className="badge">{a.type || 'tag'}</span>
                    {a.visible === false && <span className="badge badge-muted">oculto</span>}
                    {a.type === 'link' && (isValidUrl(a.mediaUrl) || isValidUrl(a.text)) && (
                      <button
                        className="mini"
                        onClick={(e) => {
                          e.stopPropagation();
                          const href = isValidUrl(a.mediaUrl) ? a.mediaUrl : a.text;
                          window.open(href, '_blank', 'noopener,noreferrer');
                        }}
                        title="Abrir enlace"
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                      </button>
                    )}
                  </div>
                  <div className="anchor-actions-row">
                    <button className="mini danger" onClick={(e)=>{ e.stopPropagation(); handleDelete(a.id); }}>
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {selected && (
            <div className="anchor-form">
              <div className="form-row">
                <label>Título</label>
                <input
                  value={selected.title}
                  onChange={(e)=>setSelected(s=>({...s, title: e.target.value}))}
                  placeholder="Etiqueta visible (solo en la lista)"
                />
              </div>

              <div className="form-row">
                <label>Tipo</label>
                <select
                  value={selected.type}
                  onChange={(e)=>setSelected(s=>({...s, type: e.target.value}))}
                >
                  <option value="tag">Etiqueta</option>
                  <option value="image">Imagen</option>
                  <option value="audio">Audio</option>
                  <option value="link">Enlace</option>
                  <option value="video">Video</option>
                </select>
              </div>

              <div className="form-row">
                <label>Texto / URL</label>
                <textarea
                  rows={3}
                  value={selected.text}
                  onChange={(e)=>setSelected(s=>({...s, text: e.target.value}))}
                  placeholder="Descripción corta, o URL si es 'link'"
                />
              </div>

              <div className="form-row">
                <label>Media URL (opcional)</label>
                <input
                  value={selected.mediaUrl || ''}
                  onChange={(e)=>setSelected(s=>({...s, mediaUrl: e.target.value}))}
                  placeholder="Imagen, audio o video (https://...)"
                />
              </div>

              <div className="form-row grid-3">
                <div>
                  <label>Posición X</label>
                  <input type="number" step="0.01" value={selected.position?.x ?? 0}
                    onChange={(e)=>setSelected(s=>({...s, position:{...s.position, x: parseFloat(e.target.value)}}))} />
                </div>
                <div>
                  <label>Y</label>
                  <input type="number" step="0.01" value={selected.position?.y ?? 0}
                    onChange={(e)=>setSelected(s=>({...s, position:{...s.position, y: parseFloat(e.target.value)}}))} />
                </div>
                <div>
                  <label>Z</label>
                  <input type="number" step="0.01" value={selected.position?.z ?? 0}
                    onChange={(e)=>setSelected(s=>({...s, position:{...s.position, z: parseFloat(e.target.value)}}))} />
                </div>
              </div>

              <div className="form-row grid-3">
                <div>
                  <label>Rotación X (pitch)</label>
                  <input type="number" step="1" value={selected.rotation?.x ?? 0}
                    onChange={(e)=>setSelected(s=>({...s, rotation:{...s.rotation, x: parseFloat(e.target.value)}}))} />
                </div>
                <div>
                  <label>Y (yaw)</label>
                  <input type="number" step="1" value={selected.rotation?.y ?? 0}
                    onChange={(e)=>setSelected(s=>({...s, rotation:{...s.rotation, y: parseFloat(e.target.value)}}))} />
                </div>
                <div>
                  <label>Z (roll)</label>
                  <input type="number" step="1" value={selected.rotation?.z ?? 0}
                    onChange={(e)=>setSelected(s=>({...s, rotation:{...s.rotation, z: parseFloat(e.target.value)}}))} />
                </div>
              </div>

              <div className="form-row">
                <label className="chk">
                  <input type="checkbox" checked={selected.visible !== false}
                    onChange={(e)=>setSelected(s=>({...s, visible: e.target.checked}))} />
                  Visible
                </label>
              </div>

              <div className="form-actions">
                <button className="btn" onClick={()=>{ setSelected(null); setPickMode(false); }}>
                  <i className="bi bi-arrow-left"></i> Cancelar
                </button>
                <button className="btn-primary" onClick={handleSave} disabled={saving}>
                  <i className="bi bi-save"></i> {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>

              <div className="hint">
                Consejo: activá <b>“Elegir con click”</b> y tocá el modelo para completar
                posición y rotación (alineadas a la superficie) con precisión de pick corregida.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Panel de detalle del hotspot activo */}
      {active && (
        <div className="anchor-detail-panel floating-card">
          <div className="detail-header">
            <div className="title">
              <i className="bi bi-info-circle"></i> {active.title || 'Accesorio'}
            </div>
            <button className="mini" onClick={() => setActive(null)} title="Cerrar">
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
          <div className="detail-body">
            {active.text && (
              <div className="detail-block">
                <div className="detail-label">Descripción</div>
                <div className="detail-text">{active.text}</div>
              </div>
            )}
            {active.type === 'image' && active.mediaUrl && (
              <div className="detail-block">
                <img src={active.mediaUrl} alt={active.title || 'imagen'} className="detail-image" />
              </div>
            )}
            {active.type === 'audio' && active.mediaUrl && (
              <div className="detail-block">
                <audio controls style={{ width: '100%' }}>
                  <source src={active.mediaUrl} />
                </audio>
              </div>
            )}
            {active.type === 'video' && active.mediaUrl && (
              <div className="detail-block">
                <video className="detail-image" style={{ background: '#000' }} controls playsInline>
                  <source src={active.mediaUrl} />
                </video>
              </div>
            )}
            {active.type === 'link' && (isValidUrl(active.mediaUrl) || isValidUrl(active.text)) && (
              <div className="detail-block">
                <a className="btn-primary" href={isValidUrl(active.mediaUrl) ? active.mediaUrl : active.text}
                   target="_blank" rel="noopener noreferrer">
                  <i className="bi bi-box-arrow-up-right"></i> Abrir enlace
                </a>
              </div>
            )}
            {active.position && (
              <div className="detail-block muted small">Posición: {vecToAttr(active.position)}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
